import { apiClient } from './api';
import type { LeaderboardEntry } from '@types';

export type LeaderboardMode = 'pve' | 'pvp';
export type LeaderboardPeriod = 'weekly' | 'monthly' | 'all-time';

export interface LeaderboardRewardTier {
  gold: number;
  maxEnergyBoost: number;
  durationDays: number;
}

export interface LeaderboardRewardsConfig {
  top1: LeaderboardRewardTier;
  top2to3: LeaderboardRewardTier;
  top4to10: LeaderboardRewardTier;
}

export interface LeaderboardResponse {
  period: LeaderboardPeriod;
  periodStart: string | null;
  periodEnd: string | null;
  entries: LeaderboardEntry[];
  rewards: LeaderboardRewardsConfig | null;
}

// Pending reward from API
export interface PendingReward {
  id: string;
  rank: number;
  period: 'weekly' | 'monthly';
  leaderboardType: 'pve' | 'pvp';
  gold: number;
  energyBoost: number;
  durationDays: number;
  periodStartDate: string;
  periodEndDate: string;
}

const ENDPOINTS: Record<LeaderboardMode, Record<LeaderboardPeriod, string>> = {
  pve: {
    weekly: '/api/v1/leaderboard/weekly',
    monthly: '/api/v1/leaderboard/monthly',
    'all-time': '/api/v1/leaderboard',
  },
  pvp: {
    weekly: '/api/v1/leaderboard/pvp/weekly',
    monthly: '/api/v1/leaderboard/pvp/monthly',
    'all-time': '/api/v1/leaderboard/pvp',
  },
};

export const leaderboardService = {
  async getLeaderboard(
    mode: LeaderboardMode = 'pve'
  ): Promise<LeaderboardEntry[]> {
    const endpoint =
      mode === 'pvp' ? '/api/v1/leaderboard/pvp' : '/api/v1/leaderboard';
    const { data } = await apiClient.get<LeaderboardResponse>(endpoint);
    return data.entries;
  },

  async getLeaderboardWithPeriod(
    mode: LeaderboardMode,
    period: LeaderboardPeriod
  ): Promise<LeaderboardResponse> {
    const endpoint = ENDPOINTS[mode][period];
    const { data } = await apiClient.get<LeaderboardResponse>(endpoint);
    return data;
  },

  async getPendingRewards(): Promise<PendingReward[]> {
    const { data } = await apiClient.get<PendingReward[]>(
      '/api/v1/user/pending-rewards'
    );
    return data;
  },

  async acknowledgeReward(rewardId: string): Promise<{ success: boolean }> {
    const { data } = await apiClient.post<{ success: boolean }>(
      `/api/v1/user/acknowledge-reward/${rewardId}`
    );
    return data;
  },

  async acknowledgeAllRewards(): Promise<{ acknowledged: number }> {
    const { data } = await apiClient.post<{ acknowledged: number }>(
      '/api/v1/user/acknowledge-all-rewards'
    );
    return data;
  },
};
